import { useRef, useState } from 'react';
import { nativeToBox, boxToNative, clampBox, type Zone } from '@/lib/prize-product';

type Props = { baseImage: string; value: Zone; onChange: (z: Zone) => void };

// Renders the garment mockup with a draggable + resizable box = the logo zone.
// Writes top-left NATIVE-pixel coords (the convention proof-email-function reads).
export function LogoZoneEditor({ baseImage, value, onChange }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [dispW, setDispW] = useState(0);
  const drag = useRef<null | { mode: 'move' | 'resize'; startX: number; startY: number; orig: Zone }>(null);

  const scale = nat && dispW ? dispW / nat.w : 1; // native * scale = displayed

  function start(mode: 'move' | 'resize', e: React.PointerEvent) {
    e.preventDefault();
    drag.current = { mode, startX: e.clientX, startY: e.clientY, orig: { ...value } };
    wrapRef.current?.setPointerCapture(e.pointerId);
  }
  function onMove(e: React.PointerEvent) {
    if (!drag.current || !nat) return;
    const dx = boxToNative(e.clientX - drag.current.startX, scale);
    const dy = boxToNative(e.clientY - drag.current.startY, scale);
    const o = drag.current.orig;
    const next: Zone = drag.current.mode === 'move'
      ? { ...o, x: o.x + dx, y: o.y + dy }
      : { ...o, logoMaxWidth: o.logoMaxWidth + dx, logoMaxHeight: o.logoMaxHeight + dy };
    onChange(clampBox(next, nat.w, nat.h));
  }
  function end() { drag.current = null; }

  const box = nat ? {
    left: nativeToBox(value.x, scale), top: nativeToBox(value.y, scale),
    width: nativeToBox(value.logoMaxWidth, scale), height: nativeToBox(value.logoMaxHeight, scale),
  } : undefined;

  return (
    <div>
      <div
        ref={wrapRef}
        className="relative inline-block touch-none select-none rounded-sm border border-border bg-bg"
        onPointerMove={onMove}
        onPointerUp={end}
        onPointerCancel={end}
      >
        <img
          src={`/api/prize-products/mockups/${encodeURIComponent(baseImage)}`}
          alt="garment mockup"
          draggable={false}
          className="block max-h-[420px] max-w-full"
          onLoad={(e) => {
            setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
            setDispW(e.currentTarget.clientWidth);
          }}
        />
        {box && (
          <div
            className="absolute cursor-move border-2 border-primary bg-primary/15"
            style={box}
            onPointerDown={(e) => start('move', e)}
          >
            <div
              className="absolute -bottom-1.5 -right-1.5 h-3 w-3 cursor-se-resize rounded-sm bg-primary"
              onPointerDown={(e) => { e.stopPropagation(); start('resize', e); }}
            />
          </div>
        )}
      </div>
      <p className="mt-1 font-mono text-[10px] text-muted">
        x={value.x} y={value.y} · {value.logoMaxWidth}×{value.logoMaxHeight}px native{nat ? ` · base ${nat.w}×${nat.h}` : ''}
      </p>
    </div>
  );
}
